/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Controller } from 'react-hook-form';

import { Dropdown } from 'primereact/dropdown';
import { Chips } from 'primereact/chips';
import { AutoComplete } from 'primereact/autocomplete';
import { InputSwitch } from 'primereact/inputswitch';
import { InputNumber } from 'primereact/inputnumber';
import { Button } from 'primereact/button';

import { useUserDetailsContext } from 'context/userDetailsContext';
import PriorityDropdown from 'components/UI/PriorityDropdown/PriorityDropdown';
import InputErrorMessage from 'components/UI/InputErrorMessage/InputErrorMessage';
import CustomFields from '../ContactForm/CustomFields';
import AddLeadSource from './AddLeadSource';
import AddCourt from './AddCourt';
import { useGlobalTagList } from 'hooks/useGlobalTags';
import { LeadStageSelect } from 'components/UI/LeadStageSelect';

import { getCaseNumberValidation, getConflictCheck } from 'services/lead/leadServices';
import { updateContactDetails } from 'services/contact';
import { useToggle } from 'hooks/useToggle';
import { generateFullName } from 'utils/generateFullNameUtils';
import { totalCountEvent } from 'services/generalServices';
import TextSnippetWrapper from 'components/TextSnippetWrapper';
import UnMappedFields from './UnMappedFields';
import { getGlobalDropdown } from 'services/caseLeadDropdownService';
import AsyncPaginate from 'components/AsyncPaginate/AsyncPaginate';
import CaseMatterSelect from 'components/UI/CaseMatterSelect/CaseMatterSelect';
import { fetchCaseMattersPage } from 'hooks/queries/useCaseMattersPaginated';
import { useReferredToContact } from 'hooks/useReferredToContact';
import ReferredOutDialog from 'modules/lead/LeadDetails/components/Overview/components/ReferredOutDialog/ReferredOutDialog';
import { gratitudeOptions } from 'constants/dropdownOptions';
import 'components/UI/shared/AsyncSelect.scss';

const CaseInformation = (props) => {
  const {
    control,
    formState: { errors },
    loadCourtContacts,
    loadLeadSources,
    loadLeadReferrals,
    setValue,
    watch,
    practiceAreaList,
    officeLocationList,
    courtList,
    leadSourceList,
    leadReferralsList,
    trigger,
    venueOptions,
    loadVenue,
    conflictCheckResult,
    setConflictCheckResult,
    practiceArea,
  } = props;

  const [filteredClients, setFilteredClients] = useState(null);
  const [showGlobalTags, setShowGlobalTags] = useState(false);
  const [tags, setTags] = useState([]);
  const [referredFromOptions, setReferredFromOptions] = useState([]);
  const [referredFromSearchQuery, setReferredFromSearchQuery] = useState('');
  const [newlyCreatedReferral, setNewlyCreatedReferral] = useState(null);
  const { formattedGlobalTag } = useGlobalTagList(practiceArea);

  const userContext = useUserDetailsContext();

  const { contactList, searchAsync, loading: contactLoading } = useReferredToContact(false, '', false, 15, true);

  const gratitudeValue = watch('lead_details.gratitude');
  const casePracticeAreaForMatter = watch('case_description.case_practice_area');
  const caseMatterField = watch('case_description.case_matter');

  const caseMatterIdForHydrate =
    typeof caseMatterField === 'string' ? caseMatterField : caseMatterField?.value ?? caseMatterField?.case_matter_id ?? '';
  const isBareCaseMatterId = typeof caseMatterField === 'string' && caseMatterField !== '';

  const { data: hydratedMatterOption } = useQuery({
    queryKey: ['caseMatterSelect', 'labelHydrate', caseMatterIdForHydrate, casePracticeAreaForMatter],
    queryFn: async () => {
      const id = caseMatterIdForHydrate;
      if (!id) return null;
      const { options } = await fetchCaseMattersPage({
        pageSize: 40,
        page: 1,
        searchQuery: id,
        practiceAreaId: casePracticeAreaForMatter || undefined,
      });
      return options.find((o) => String(o?.value ?? o?.case_matter_id) === id) ?? null;
    },
    enabled: isBareCaseMatterId && Boolean(caseMatterIdForHydrate),
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    if (!hydratedMatterOption || !isBareCaseMatterId) return;
    const id = String(caseMatterField);
    if (String(hydratedMatterOption.value ?? hydratedMatterOption.case_matter_id) !== id) return;
    const stored = hydratedMatterOption
      ? {
          value: hydratedMatterOption.case_matter_id || hydratedMatterOption.value,
          label: hydratedMatterOption.matter_name || hydratedMatterOption.label,
          case_matter_id: hydratedMatterOption.case_matter_id || hydratedMatterOption.value,
          matter_name: hydratedMatterOption.matter_name || hydratedMatterOption.label,
        }
      : null;
    if (stored) {
      setValue('case_description.case_matter', stored, { shouldDirty: false, shouldValidate: false });
    }
  }, [hydratedMatterOption, isBareCaseMatterId, caseMatterField, setValue]);

  const bareMatterSelectFallback = useMemo(() => {
    if (!isBareCaseMatterId) return null;
    return {
      value: caseMatterField,
      label: caseMatterField,
      matter_name: caseMatterField,
      case_matter_id: caseMatterField,
    };
  }, [isBareCaseMatterId, caseMatterField]);

  const [courtToggle, setToggle] = useToggle();
  const [sourceToggle, setSourceToggle] = useToggle();
  const [onSourceReferral, setOnSourceReferral] = useToggle();
  const [referralToggle, setReferralToggle] = useToggle();
  const [toggleVenue, setToggleVenue] = useToggle();

  // Define your chip template function
  const chipTemplate = (chip) => (
    <div className="p-chip">
      <span>#{chip}</span>
    </div>
  );

  useEffect(() => {
    const leadSourceValue = watch('lead_details.source');
    const referralSourceId = leadSourceList?.find((item) => item?.source?.toLowerCase() === 'referral')?.lead_source_id;

    if (referralSourceId && leadSourceValue === referralSourceId) {
      setOnSourceReferral(true);
    } else if (referralSourceId) {
      setOnSourceReferral(false);
    }
  }, [leadSourceList]);

  const validateCaseNumber = async (value) => {
    if (!Boolean(value)) {
      return true;
    }
    return new Promise(async (resolve) => {
      const response = await getCaseNumberValidation(value);
      if (response?.data?.length === 0) {
        resolve(true);
      } else resolve('Duplicate Case Number');
    });
  };

  const searchClient = async (event) => {
    let totalCount = await totalCountEvent('contact');
    const response = await getGlobalDropdown('', 'contact', event.query, null, false, 1, totalCount.data);

    // if (response?.data?.contacts?.length > 0) {
    if (response?.data?.data?.length > 0) {
      let data = [];
      // response.data.contacts.forEach((element) => {
      response.data.data.forEach((element) => {
        data.push({
          label: element.contact_type === 'person' ? generateFullName(element) : element.title,
          value: element.contact_id,
          client: element,
        });
      });

      const sortedData = data.sort((a, b) => {
        const aName =
          a.client?.contact_type === 'person' ? a.client?.first_name?.toLowerCase() || '' : a.client?.title?.toLowerCase() || '';
        const bName =
          b.client?.contact_type === 'person' ? b.client?.first_name?.toLowerCase() || '' : b.client?.title?.toLowerCase() || '';
        return aName.localeCompare(bName);
      });

      setFilteredClients(sortedData);
    } else {
      setFilteredClients([]);
    }
  };

  const courtAddOnSuccess = (data) => {
    loadCourtContacts();
    setToggle(false);
    if (data) {
      // Small delay to ensure courtList is updated before setting the value
      setTimeout(() => {
        setValue('case_description.court', data.contact_id);
      }, 100);
    }
  };

  const venueAddOnSuccess = (data) => {
    loadVenue();
    setToggleVenue(false);
    if (data) {
      // Small delay to ensure venueOptions is updated before setting the value
      setTimeout(() => {
        setValue('case_description.venue', data.venue_id);
      }, 100);
    }
  };

  const sourceAddOnSuccess = (data) => {
    loadLeadSources();
    setSourceToggle(false);
    if (data) {
      // Small delay to ensure leadSourceList is updated before setting the value
      setTimeout(() => {
        setValue('lead_details.source', data.source_id);
      }, 100);
    }
  };

  const loadReferralOptions = async (inputValue, loadedOptions, additional) => {
    const { pageLimit, pagination_token, pagination_direction } = additional;
    const searchQuery = inputValue || '';

    if (!pagination_token) {
      setReferredFromSearchQuery(searchQuery);
    }

    const response = await searchAsync({ query: searchQuery }, pagination_token, pagination_direction, pageLimit);

    const formattedOptions = (response?.contacts || []).map((contact) => ({
      label:
        contact.contact_type === 'person'
          ? `${contact.title || ''} ${contact.first_name} ${contact.last_name}`.trim()
          : contact.title || contact.business_name || 'Business Contact',
      value: contact.contact_id,
      contact_id: contact.contact_id,
      ...contact,
    }));

    if (!pagination_token) {
      setReferredFromOptions(formattedOptions);
    } else {
      setReferredFromOptions((prevOptions) => [...prevOptions, ...formattedOptions]);
    }

    return {
      options: formattedOptions,
      hasMore: response?.next_token ? true : false,
      nextToken: response?.next_token || '',
      transitionToken: response?.transition_token || '',
      additional: {
        pageLimit: pageLimit,
        pagination_token: response?.next_token || '',
        pagination_direction: pagination_direction,
      },
    };
  };

  useEffect(() => {
    if (referredFromOptions?.length === 0) {
      setReferredFromOptions(contactList);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactList]);

  const referralAddOnSuccess = (contactId, contactData) => {
    if (contactId) {
      setValue('referred_from', contactId);
      if (contactData) {
        setNewlyCreatedReferral({
          label:
            contactData.contact_type === 'person'
              ? `${contactData.title || ''} ${contactData.first_name || ''} ${contactData.last_name || ''}`.trim()
              : contactData.title || contactData.business_name || 'Business Contact',
          value: contactData.contact_id,
          contact_id: contactData.contact_id,
          ...contactData,
        });
      }
    }
    setReferralToggle(false);
  };

  const onSelectContact = (e) => {
    if (e.value && e.value.value) {
      getConflictCheck(e.value.value)
        .then((response) => {
          setConflictCheckResult(response.data);
          if (response.data?.message && response.data?.message === 'This contact has conflict') {
            setValue('lead_details.conflict_check', true);
          }
          if (response.data?.message && response.data?.message === 'No conflicts') {
            setValue('lead_details.conflict_check', false);
          }
        })
        .catch((error) => {});
    }
  };

  const onClearContact = () => {
    setConflictCheckResult('');
    setValue('lead_details.conflict_check', false);
    setValue('lead_details.conflict_note', '');
  };

  const handleTagChange = (e, field) => {
    const uniqueTags = [...new Set(e.value)];
    setTags(uniqueTags);
    setValue('lead_details.tags', uniqueTags);
    field.onChange(uniqueTags);
  };

  return (
    <div className="container-fluid">
      <div className="row my-4">
        <div className="col-12 col-md-6">
          <div className="row">
            <div className="col">
              <h5 className="caption-bold">CASE</h5>
              <div className="card-bg lead-data-box p-3 d-flex align-items-center mb-3 flex-wrap">
                <div className="col-md-4 col-12 p-medium">Potential Case Value</div>
                <div className="col-md-8 col-12">
                  <Controller
                    name="case_description.potential_case_value"
                    control={control}
                    render={({ field }) => (
                      <InputNumber
                        className="w-100 input-shadow"
                        inputId="currency-us"
                        placeholder="Type in a case value"
                        mode="currency"
                        currency="USD"
                        locale="en-US"
                        min={0}
                        maxFractionDigits={2}
                        id={field.name}
                        onKeyDown={(e) => {
                          if (!/^\d$/.test(e.key)) return;
                          const input = e.target;
                          if (input.selectionStart !== input.selectionEnd) return;
                          const raw = input.value.replace(/[^0-9.]/g, '');
                          const intDigits = raw.split('.')[0]?.length || 0;
                          const dotPos = input.value.indexOf('.');
                          const cursorInDecimal = dotPos !== -1 && input.selectionStart > dotPos;
                          if (!cursorInDecimal && intDigits >= 10) e.preventDefault();
                        }}
                        onChange={(e) => {
                          if (e.value != null && e.value > 9999999999.99) return;
                          field.onChange(e.value);
                        }}
                        value={field.value}
                      />
                    )}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="row mt-2 pt-2">
            <div className="col">
              <h5 className="caption-bold">POTENTIAL CASE DETAILS</h5>
              <div className="card-bg lead-data-box p-3">
                <div className="col">
                  <div className="d-flex align-items-center py-2 flex-wrap">
                    <div className="col-md-4 col-12 p-medium">Practice Area</div>
                    <div className="col-md-8 col-12">
                      <Controller
                        name="case_description.case_practice_area"
                        control={control}
                        render={({ field }) => (
                          <Dropdown
                            id={field.name}
                            onChange={(e) => {
                              setValue('case_description.case_matter', '');
                              setValue('leadStageSk', { lead_stage_name: 'New' });
                              field.onChange(e.value);
                            }}
                            value={field.value}
                            options={practiceAreaList}
                            optionLabel="practice_area_name"
                            optionValue="practice_area_id"
                            className="input-shadow border-0 w-100"
                            placeholder="Select Practice Area"
                            filter
                          />
                        )}
                      />
                    </div>
                  </div>
                  <div className="d-flex align-items-center py-2 flex-wrap">
                    <div className="col-md-4 col-12 p-medium">Case Matter</div>
                    <div className="col-md-8 col-12">
                      <Controller
                        name="case_description.case_matter"
                        control={control}
                        render={({ field }) => {
                          const selectValue = (() => {
                            const v = field.value;
                            if (v == null || v === '') return null;
                            if (typeof v === 'object' && (v.value != null || v.case_matter_id != null)) return v;
                            if (typeof v === 'string' && hydratedMatterOption) return hydratedMatterOption;
                            return bareMatterSelectFallback;
                          })();
                          return (
                            <CaseMatterSelect
                              name={field.name}
                              id={field.name}
                              className="input-shadow border-0 w-100"
                              placeholder="Select Case Matter"
                              practiceAreaId={casePracticeAreaForMatter || ''}
                              value={selectValue}
                              onChange={(opt) => {
                                if (opt == null) {
                                  field.onChange('');
                                  return;
                                }
                                const stored = opt
                                  ? {
                                      value: opt.case_matter_id || opt.value,
                                      label: opt.matter_name || opt.label,
                                      case_matter_id: opt.case_matter_id || opt.value,
                                      matter_name: opt.matter_name || opt.label,
                                    }
                                  : null;
                                field.onChange(stored ?? '');
                              }}
                              isClearable
                              cacheUniqs={[String(casePracticeAreaForMatter ?? '')]}
                              menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                              menuPosition="fixed"
                              styles={{
                                menuPortal: (base) => ({ ...base, zIndex: 11000 }),
                              }}
                            />
                          );
                        }}
                      />
                    </div>
                  </div>
                  <div className="d-flex align-items-center py-2 flex-wrap">
                    <div className="col-md-4 col-12 p-medium">Case Number</div>
                    <div className="col-md-8 col-12">
                      <Controller
                        name={`case_description.case_number`}
                        rules={{ validate: { asyncValidation: validateCaseNumber } }}
                        defaultValue={''}
                        control={control}
                        render={({ field }) => (
                          <TextSnippetWrapper
                            type="text"
                            placeholder="Type in"
                            {...field}
                            onBlur={() => trigger('case_description.case_number')}
                            onChange={(e) => setValue('case_description.case_number', e.target.value)}
                            className="input-shadow border-0 w-100"
                          />
                        )}
                      />
                      {errors?.case_description?.case_number && <InputErrorMessage>Case Number Already Exist</InputErrorMessage>}
                    </div>
                  </div>
                  <div className="d-flex align-items-center py-2 flex-wrap">
                    <div className="col-md-4 col-12 p-medium">Case Name</div>
                    <div className="col-md-8 col-12">
                      <Controller
                        name={`case_description.case_name`}
                        defaultValue={''}
                        control={control}
                        render={({ field }) => (
                          <TextSnippetWrapper type="text" placeholder="Type in" {...field} className="input-shadow border-0 w-100" />
                        )}
                      />
                    </div>
                  </div>
                  <div className="d-flex align-items-center py-2 flex-wrap">
                    <div className="col-md-4 col-12 p-medium">Court</div>
                    <div className="col-md-8 col-12 d-flex align-items-center gap-2">
                      <div className="flex-1">
                        {courtToggle ? (
                          <AddCourt onSuccess={courtAddOnSuccess} onDismiss={setToggle} isCourt={true} />
                        ) : (
                          <Controller
                            name="case_description.court"
                            control={control}
                            render={({ field }) => (
                              <Dropdown
                                id={field.name}
                                onChange={(e) => field.onChange(e.value)}
                                value={field.value}
                                options={courtList}
                                optionValue="contact_id"
                                optionLabel="title"
                                placeholder="Select"
                                className="input-shadow w-100"
                                filter
                              />
                            )}
                          />
                        )}
                      </div>
                      {userContext?.permissions?.settings?.add_court_location && (
                        <div className="d-flex align-items-center justify-content-end">
                          <span className="pointer">
                            <i className="fas fa-plus-circle" onClick={setToggle} />
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="d-flex align-items-center py-2 flex-wrap">
                    <div className="col-md-4 col-12 p-medium">Venue</div>
                    <div className="col-md-8 col-12 d-flex align-items-center gap-2">
                      <div className="flex-1">
                        {toggleVenue ? (
                          <AddCourt onSuccess={venueAddOnSuccess} onDismiss={setToggleVenue} isCourt={false} />
                        ) : (
                          <Controller
                            name="case_description.venue"
                            control={control}
                            render={({ field }) => (
                              <Dropdown
                                id={field.name}
                                onChange={(e) => field.onChange(e.value)}
                                value={field.value}
                                options={venueOptions}
                                optionValue="venue_id"
                                optionLabel="venue_name"
                                placeholder="Select"
                                className="input-shadow w-100"
                                filter
                              />
                            )}
                          />
                        )}
                      </div>
                      <div className="d-flex align-items-center justify-content-end">
                        <span className="pointer">
                          <i className="fas fa-plus-circle" onClick={setToggleVenue} />
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="d-flex py-2 flex-wrap">
                    <div className="col-md-4 col-12 p-medium">Description</div>
                    <div className="col-md-8 col-12">
                      <Controller
                        name={`lead_details.description`}
                        defaultValue={''}
                        control={control}
                        render={({ field }) => (
                          <TextSnippetWrapper
                            type="textarea"
                            placeholder="Description"
                            {...field}
                            className="input-shadow border-0 w-100 p-2 "
                            rows="8"
                          />
                        )}
                      />
                    </div>
                  </div>
                  <div className="d-flex align-items-center py-2 flex-wrap">
                    <div className="col-md-4 col-12 p-medium">Tags</div>
                    <div className="col-md-8 col-12">
                      <Controller
                        name="lead_details.tags"
                        control={control}
                        render={({ field }) => (
                          <Chips
                            id={field.name}
                            onChange={(e) => handleTagChange(e, field)}
                            value={field.value}
                            itemTemplate={chipTemplate}
                            placeholder="Select Tags"
                            className="border-0 w-100 taging"
                          />
                        )}
                      />
                    </div>
                  </div>
                  <div className="col-12">
                    <div className="row mb-2">
                      <div className="col-sm-12 addfield">
                        <Button
                          type="button"
                          className="p-button button-text text-medium addfield ps-0"
                          onClick={(e) => {
                            e.preventDefault();
                            setShowGlobalTags(!showGlobalTags);
                          }}
                        >
                          {showGlobalTags ? <i className="pi pi-minus F-size14 me-1"> </i> : <i className="pi pi-plus F-size14 me-1" />}Add
                          Global Tags
                        </Button>
                      </div>
                    </div>
                    {showGlobalTags && (
                      <div className="row mb-3">
                        <div className="col-sm-4"></div>
                        {formattedGlobalTag?.length > 0 ? (
                          <div className="col-sm-7">
                            <div className="global-tag-wrapper input-shadow">
                              {formattedGlobalTag.map((element, index) => {
                                let hashReplacedTag = element.value.replace('#', '');
                                return (
                                  <span
                                    key={index}
                                    className={`tag-highlight ${tags.includes(hashReplacedTag) ? 'selected-global-tag' : ''}`}
                                    onClick={(e) => {
                                      let existingTags = watch('lead_details.tags');
                                      existingTags?.push(hashReplacedTag);
                                      setTags([...new Set(existingTags)]);
                                      setValue('lead_details.tags', [...new Set(existingTags)]);
                                    }}
                                  >
                                    {element.value}
                                  </span>
                                );
                              })}
                            </div>
                          </div>
                        ) : (
                          <div className="row mb-2">
                            <div className="col-12 small">
                              <i className="fas fa-info-circle mx-2 mt-1"></i>
                              <span className="text-hint">No global tags found.</span>{' '}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  {userContext.checkSubPermission('custom_fields') && (
                    <>
                      <div className="d-flex align-items-center py-2">
                        <Controller
                          name="case_description.custom_field_toggle"
                          control={control}
                          defaultValue={false}
                          render={({ field }) => (
                            <Button
                              id={field.name}
                              type="button"
                              className="p-button button-text text-medium addfield ps-0"
                              onClick={(e) => field.onChange(!field.value)}
                            >
                              <i className={field.value ? 'pi pi-minus F-size14 me-1' : 'pi pi-plus F-size14 me-1'}></i>Add Custom Fields
                            </Button>
                          )}
                        />
                      </div>
                      {watch('case_description.custom_field_toggle') && (
                        <CustomFields
                          field="case_description.custom_field"
                          control={control}
                          practiceArea={watch('case_description.case_practice_area')}
                        />
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-6 col-12 mt-3 mt-md-0">
          <div className="row">
            <div className="col-sm-12">
              <h5 className="caption-bold">DETAILS</h5>
              <div className="row card-bg lead-data-box p-3">
                <div className="col">
                  <div className="d-flex align-items-center py-2 flex-wrap">
                    <div className="col-md-4 col-12 p-medium">Lead Priority</div>
                    <div className="col-md-8 col-12">
                      <Controller
                        name="lead_details.priority"
                        control={control}
                        render={({ field }) => (
                          <PriorityDropdown
                            id={field.name}
                            onChange={(e) => field.onChange(e.value)}
                            value={field.value}
                            className="input-shadow w-100 input-height"
                            filter
                            placeholder="Select	Priority"
                          />
                        )}
                      />
                    </div>
                  </div>
                  <div className="d-flex align-items-center py-2 flex-wrap">
                    <div className="col-md-4 col-12 p-medium">Lead Source</div>
                    <div className="col-md-8 col-12 d-flex align-items-center gap-2">
                      <div className="flex-1">
                        {sourceToggle ? (
                          <AddLeadSource onSuccess={sourceAddOnSuccess} onDismiss={setSourceToggle} />
                        ) : (
                          <Controller
                            name="lead_details.source"
                            control={control}
                            render={({ field }) => (
                              <Dropdown
                                id={field.name}
                                onChange={(e) => {
                                  let lead_source_id = leadSourceList?.find(
                                    (item) => item?.source?.toLowerCase() === 'referral'
                                  )?.lead_source_id;
                                  if (lead_source_id && e.value === lead_source_id) {
                                    setOnSourceReferral(true);
                                  } else {
                                    setOnSourceReferral(false);
                                    setReferralToggle(false);
                                    setValue('referred_from', null);
                                  }
                                  field.onChange(e.value);
                                }}
                                value={field.value}
                                optionLabel="source"
                                optionValue="lead_source_id"
                                options={leadSourceList}
                                placeholder="Select"
                                className="input-shadow w-100"
                                filter
                              />
                            )}
                          />
                        )}
                      </div>
                      <div className="d-flex align-items-center justify-content-end">
                        <span className="pointer">
                          <i className="fas fa-plus-circle" onClick={setSourceToggle} />
                        </span>
                      </div>
                    </div>
                  </div>
                  {onSourceReferral && (
                    <div className="d-flex align-items-center py-2 flex-wrap">
                      <div className="col-lg-4 col-12 p-medium">Referred From</div>
                      <div className="col-md-8 col-12 d-flex align-items-center gap-2">
                        <div className="flex-1">
                          {referralToggle ? (
                            <ReferredOutDialog
                              onHide={() => setReferralToggle(false)}
                              leadDetails={{ is_lead: true }}
                              onSuccess={referralAddOnSuccess}
                            />
                          ) : (
                            <Controller
                              name="referred_from"
                              control={control}
                              render={({ field }) => {
                                const selectedOption =
                                  referredFromOptions?.find((item) => item?.contact_id === field?.value || item?.value === field?.value) ||
                                  (newlyCreatedReferral?.contact_id === field?.value ? newlyCreatedReferral : null) ||
                                  null;

                                return (
                                  <AsyncPaginate
                                    name={field.name}
                                    value={selectedOption}
                                    onChange={(selectedOption) => {
                                      const contactId = selectedOption?.contact_id || selectedOption?.value || null;
                                      field.onChange(contactId);
                                      if (contactId && selectedOption) {
                                        const { label, value, ...contactData } = selectedOption;
                                        updateContactDetails({
                                          ...contactData,
                                          referral_values: ['INBOUND'],
                                        });
                                      }
                                    }}
                                    loadOptions={loadReferralOptions}
                                    getOptionLabel={(option) =>
                                      option?.label ||
                                      `${option?.title || ''} ${option?.first_name || ''} ${option?.last_name || ''}`.trim() ||
                                      option?.business_name ||
                                      'Contact'
                                    }
                                    getOptionValue={(option) => option?.value || option?.contact_id}
                                    additional={{
                                      pageLimit: 15,
                                      pagination_token: '',
                                      pagination_direction: 'forward',
                                    }}
                                    cacheUniqs={[referredFromSearchQuery, contactList?.length]}
                                    debounceTimeout={300}
                                    placeholder="Select"
                                    className="input-shadow w-100"
                                    classNamePrefix="async-select-custom"
                                    isLoading={contactLoading}
                                  />
                                );
                              }}
                            />
                          )}
                        </div>
                        <div className="d-flex align-items-center justify-content-end">
                          <span className="pointer">
                            <i className="fas fa-plus-circle" onClick={() => setReferralToggle(true)} />
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                  {onSourceReferral && (
                    <div className="d-flex align-items-center py-2 flex-wrap">
                      <div className="col-md-4 col-12 p-medium">Gratitude</div>
                      <div className="col-md-8 col-12">
                        <Controller
                          name="lead_details.gratitude"
                          control={control}
                          render={({ field }) => (
                            <Dropdown
                              id={field.name}
                              onChange={(e) => field.onChange(e.value)}
                              value={field.value}
                              options={gratitudeOptions}
                              optionLabel="label"
                              optionValue="value"
                              placeholder="Select"
                              className="input-shadow w-100"
                              filter
                            />
                          )}
                        />
                      </div>
                    </div>
                  )}
                  {onSourceReferral && gratitudeValue === 'yes' && (
                    <div className="d-flex align-items-center py-2 flex-wrap">
                      <div className="col-md-4 col-12 p-medium">Gratitude Item</div>
                      <div className="col-md-8 col-12">
                        <Controller
                          name="lead_details.gratitude_item"
                          control={control}
                          render={({ field }) => (
                            <TextSnippetWrapper type="text" placeholder="Type in" {...field} className="input-shadow w-100" />
                          )}
                        />
                      </div>
                    </div>
                  )}
                  <div className="d-flex align-items-center py-2 flex-wrap">
                    <div className="col-md-4 col-12 p-medium">Lead Stage</div>
                    <div className="col-md-8 col-12">
                      <Controller
                        name="leadStageSk"
                        control={control}
                        render={({ field }) => (
                          <LeadStageSelect
                            key={casePracticeAreaForMatter || 'all'}
                            name={field.name}
                            value={field.value}
                            onChange={(option) => field.onChange(option)}
                            placeholder="Select"
                            practiceAreaId={casePracticeAreaForMatter || ''}
                            show_unassigned_pa_column={casePracticeAreaForMatter ? true : undefined}
                            isOptionDisabled={(option) => option?.lead_stage_name?.toLowerCase() === 'closed'}
                            className="input-shadow border-0 w-100"
                            isClearable={false}
                            cacheUniqs={[casePracticeAreaForMatter || 'all']}
                            menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                            menuPosition="fixed"
                          />
                        )}
                      />
                    </div>
                  </div>

                  <div className="d-flex align-items-top py-2 flex-wrap">
                    <div className="col-md-4 col-12 p-medium line_height3">Conflict Check</div>
                    <div className="col-md-8 col-12">
                      <div className="mb-3">
                        <Controller
                          name="lead_details.conflict_contacts"
                          control={control}
                          render={({ field }) => (
                            <AutoComplete
                              placeholder="Enter Name"
                              style={{ width: '100%' }}
                              inputStyle={{ width: '100%' }}
                              value={field.value}
                              suggestions={filteredClients}
                              onSelect={(e) => onSelectContact(e)}
                              onClear={onClearContact} // Add onClear handler
                              completeMethod={searchClient}
                              field="label"
                              onChange={(e) => field.onChange(e.value)}
                              className="flex-grow-1 input-shadow"
                            />
                          )}
                        />
                      </div>
                      <div className="mb-3 d-flex align-items-center">
                        <Controller
                          name="lead_details.conflict_check"
                          control={control}
                          defaultValue={false}
                          render={({ field }) => (
                            <InputSwitch
                              className="input-swich"
                              inputId={field.name}
                              checked={field.value}
                              onChange={(e) => field.onChange(e.value)}
                            />
                          )}
                        />
                        {conflictCheckResult?.message === 'This contact has conflict' ? (
                          <label className="p-medium ms-2">Conflict</label>
                        ) : (
                          <label className="p-medium ms-2">No Conflict</label>
                        )}
                      </div>
                      {!!conflictCheckResult?.case_data?.length &&
                        conflictCheckResult?.case_data?.map((data, i) => {
                          return data?.is_lead === true ? (
                            <div className="p-2" key={i}>
                              <Link
                                to={`/leads/${data?.case_id}`}
                                target="_blank"
                                className="text-break pointer F-size14 call-back text-decoration-none"
                              >
                                {data?.case_description?.case_name}
                              </Link>
                            </div>
                          ) : (
                            <div className="p-2" key={i}>
                              <Link
                                to={`/cases/${data?.case_id}`}
                                target="_blank"
                                className="text-break pointer F-size14 call-back text-decoration-none"
                              >
                                {data?.case_description?.case_name}
                              </Link>
                            </div>
                          );
                        })}
                    </div>
                  </div>
                  <div className="d-flex align-items-top py-2 F-size14 flex-wrap">
                    <div className="col-md-4 col-12 text-muted p-medium">Conflict Notes</div>
                    <div className="col-md-8 col-12 d-flex align-items-center">
                      <Controller
                        name="lead_details.conflict_note"
                        control={control}
                        render={({ field }) => (
                          <TextSnippetWrapper
                            type="textarea"
                            rows={8}
                            cols={30}
                            placeholder="Enter Conflict Notes"
                            value={field.value}
                            onChange={(e) => {
                              field.onChange(e.target.value);
                            }}
                            className="input-shadow w-100"
                          />
                        )}
                      />
                    </div>
                  </div>
                  {officeLocationList?.length === 1 ? null : (
                    <div className="d-flex align-items-center py-2 flex-wrap">
                      <div className="col-md-4 col-12 p-medium">Office Location</div>
                      <div className="col-md-8 col-12">
                        <Controller
                          name="case_description.office_location"
                          control={control}
                          render={({ field }) => (
                            <Dropdown
                              id={field.name}
                              onChange={(e) => field.onChange(e.value)}
                              value={field.value}
                              placeholder="Select Location"
                              options={officeLocationList}
                              optionValue="office_location_id"
                              optionLabel="office_name"
                              className="input-shadow border-0 w-100"
                              filter
                            />
                          )}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          {watch('online_lead_fields') != null && (
            <div className="row mt-2 pt-2">
              <div className="col">
                <h5 className="caption-bold">ONLINE FORM ANSWERS</h5>
                <div className="card-bg lead-data-box p-3">
                  <div className="col">
                    <UnMappedFields field="online_lead_fields" control={control} setValue={setValue} watch={watch} errors={errors} />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
export default CaseInformation;
